import React, { useState } from 'react';
import { useInView } from '../hooks/useInView';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          organization: '',
          message: ''
        });
      } else {
        console.error('Email error:', result.error);
        console.error('Response status:', response.status);
        console.error('Response headers:', response.headers);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Network error:', error);
      console.error('Error details:', error.message);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const [headingRef, headingInView] = useInView();
  const [formRef, formInView] = useInView();
  const [infoRef, infoInView] = useInView();

  const contactMethods = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      title: "Email",
      description: "Send us a message and we'll get back to you within 24 hours.",
      contact: "contact@choralplayer.com",
      action: "mailto:contact@choralplayer.com"
    }
  ];

  return (
    <section id="contact" className="py-20 px-4 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto">
        <div
          ref={headingRef}
          className={`text-center mb-16 ${headingInView ? 'animate-fade-up' : 'opacity-0'}`}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Get in
            <span className="gradient-text"> Touch</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ready to transform your choir's practice experience? We'd love to hear from you
            and help you get started with ChoralPlayer.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div
            ref={formRef}
            className={`card ${formInView ? 'animate-fade-up' : 'opacity-0'}`}
          >
            <h3 className="text-2xl font-bold mb-6">Send us a Message</h3>
            
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800">Thank you! Your message has been sent successfully. We'll get back to you soon.</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800">Sorry, there was an error sending your message. Please try again or contact us directly.</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Your full name"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-2">
                  Organization
                </label>
                <input
                  type="text"
                  id="organization"
                  name="organization"
                  value={formData.organization}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Your choir or organization"
                />
              </div>

              

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Tell us about your choir and how we can help you..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn btn-primary text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div
            ref={infoRef}
            className={`space-y-8 ${infoInView ? 'animate-fade-up' : 'opacity-0'}`}
            style={{ animationDelay: infoInView ? '150ms' : undefined }}
          >
            <div>
              <h3 className="text-2xl font-bold mb-6">Other Ways to Reach Us</h3>
              <div className="space-y-6">
                {contactMethods.map((method, index) => (
                  <div key={index} className="flex items-start space-x-4 p-4 bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center text-white flex-shrink-0">
                      {method.icon}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-lg mb-1">{method.title}</h4>
                      <p className="text-gray-600 mb-2">{method.description}</p>
                      <a
                        href={method.action}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {method.contact}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            

            <div className="card">
              <h4 className="text-xl font-bold mb-4">Frequently Asked Questions</h4>
              <div className="space-y-4">
                <div>
                  <h5 className="font-semibold text-gray-800">How quickly can you create practice tracks?</h5>
                  <p className="text-gray-600 text-sm">Most tracks are typically completed within 3-5 business days. For more advanced tracks, however, this could take longer. We'll give you an estimate when you send in your score.</p>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-800">Do you work with all types of choirs?</h5>
                  <p className="text-gray-600 text-sm">Yes! From church choirs to professional ensembles, we serve all levels. We love to help all choirs, no matter the size.</p>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-800">Do I have to install an app?</h5>
                  <p className="text-gray-600 text-sm">Nope! ChoralPlayer is a progressive web app, meaning you can access it from the web on any device or browser! You can also add it to your home screen on your phone.</p>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-800">What file formats do you accept?</h5>
                  <p className="text-gray-600 text-sm">We only accept PDF sheet music for the time being to ensure the highest quality tracks.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact; 